import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import routes from '../Constants/routes';
import bankruptcyTerminationActions from '../Actions/bankruptcyTerminationActions';
import JobStatusBadge from '../Components/BankruptcyTermination/JobStatusBadge';
import SummaryBar from '../Components/BankruptcyTermination/SummaryBar';
import CustomerRow from '../Components/BankruptcyTermination/CustomerRow';
import Pagination from '../Components/BankruptcyTermination/Pagination';
import formattingUtils from '../Utils/formattingUtils';
import dateUtils from '../Utils/dateUtils';
import type {BankruptcyCustomerResultDto, BankruptcyTerminationState} from './BankruptcyTermination.types';
import ExpandableTable from '../Components/Fundamentals/Tables/ExpandedableTable';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ET = ExpandableTable as any;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyDispatch = any;

interface RootState {
    BankruptcyTerminationReducer: BankruptcyTerminationState;
}

const CUSTOMERS_PAGE_SIZE = 50;

const BankruptcyTerminationDetailContainer = () => {
    const dispatch: AnyDispatch = useDispatch();
    const { jobId } = useParams<{ jobId: string }>();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const jobDetail = useSelector((state: RootState) => state.BankruptcyTerminationReducer.jobDetail);
    const customerResults = useSelector((state: RootState) => state.BankruptcyTerminationReducer.customerResults);
    const totalPages = useSelector((state: RootState) => state.BankruptcyTerminationReducer.customerResultsTotalPages);
    const [page, setPage] = useState(1);

    useEffect(() => {
        if (Boolean(jobId)) {
            const actions = bindActionCreators(bankruptcyTerminationActions, dispatch);
            actions.getJobDetail(Number(jobId), page, CUSTOMERS_PAGE_SIZE);
        }
    }, [dispatch, jobId, page]);

    const isCurrentJob = jobDetail?.jobId === Number(jobId);
    const pagedCustomers = customerResults ?? [];

    return (
        <div className="container-fluid">
            <div className="d-flex align-items-center mb-3 gap-3">
                <button
                    className="btn btn-default btn-sm"
                    onClick={() => { void navigate(routes.bankruptcyTermination); }}
                >
                    <i className="vismaicon vismaicon-sm vismaicon-arrow-left-circle" />
                    {t('bankruptcytermination.backtolist')}
                </button>
                <h2 className="mb-0">
                    {t('bankruptcytermination.jobtitle', { id: jobId })}
                    {/* eslint-disable-next-line @typescript-eslint/strict-boolean-expressions */}
                    {jobDetail && isCurrentJob && (
                        <>
                            <span className="ml-4"><JobStatusBadge status={jobDetail.status} /></span>
                            {/* eslint-disable-next-line @typescript-eslint/strict-boolean-expressions */}
                            {jobDetail.startedAt && (
                                <small className="ml-2">
                                    {dateUtils.formatDateAndTime(jobDetail.startedAt)}
                                    {jobDetail.duration !== null && jobDetail.duration !== undefined && ` · ${formattingUtils.formatDuration(jobDetail.duration)}`}
                                </small>
                            )}
                        </>
                    )}
                </h2>
            </div>

            {/* eslint-disable-next-line @typescript-eslint/strict-boolean-expressions */}
            {!jobDetail || !isCurrentJob || !customerResults ? (
                <div className="spinner spinner-default-green" />
            ) : (
                <>
                    {Boolean(jobDetail.errorMessage) && (
                        <div className="alert alert-danger mb-3">{jobDetail.errorMessage}</div>
                    )}

                    <SummaryBar summary={jobDetail.summary} />

                    {customerResults.length === 0 ? (
                        <p>{t('bankruptcytermination.nocustomers')}</p>
                    ) : (
                        <>
                            <ET>
                                <thead>
                                    <tr>
                                        <th>{t('bankruptcytermination.orgnumber')}</th>
                                        <th>{t('bankruptcytermination.customername')}</th>
                                        <th>{t('bankruptcytermination.customernumber')}</th>
                                        <th>{t('bankruptcytermination.companystatuscode')}</th>
                                        <th>{t('bankruptcytermination.companystatusname')}</th>
                                        <th>{t('bankruptcytermination.companystatusdate')}</th>
                                        <th>{t('bankruptcytermination.customerupdatestatus')}</th>
                                        <th>{t('bankruptcytermination.subscriptions')}</th>
                                        <th>{t('bankruptcytermination.processedat')}</th>
                                        <th>{t('bankruptcytermination.status')}</th>
                                        <th>{t('bankruptcytermination.hasunpaidinvoices')}</th>
                                        <th>{t('bankruptcytermination.hasvbproducts')}</th>
                                        <th>{t('bankruptcytermination.emailsent')}</th>
                                        <th>{t('bankruptcytermination.manuallyhandled')}</th>
                                    </tr>
                                </thead>
                                <ET.Body>
                                    {pagedCustomers.map((customer: BankruptcyCustomerResultDto) => (
                                        <CustomerRow key={customer.id} customer={customer} />
                                    ))}
                                </ET.Body>
                            </ET>
                            <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default BankruptcyTerminationDetailContainer;
